import React, { useEffect, useState } from 'react';
import { Card, Table, Typography, Tag, Space, Button, message, Modal, Form, Input, Image, Select, Row, Col } from 'antd';
import { FacebookOutlined, ReloadOutlined, PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, PictureOutlined, VideoCameraOutlined, FileTextOutlined, SaveOutlined } from '@ant-design/icons';
import { Facebook, FacebookPost, FacebookResponse, FacebookPostDetail, SocialMediaPost } from '../types/facebook';
import { FacebookService } from '../services/facebookService';
import { SocialMediaPostService } from '../services/socialMediaPostService';
import axios from 'axios';
import dayjs from 'dayjs';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import '../styles/FacebookManagement.css';
import type { ColumnsType } from 'antd/es/table';

const { Title, Paragraph } = Typography;
const { Search } = Input;
const { confirm } = Modal;

const getPostType = (post: FacebookPost) => {
  if (!post.attachments) {
    return 'text';
  }

  const attachment = post.attachments.data[0];
  if (!attachment) {
    return 'text';
  }

  if (attachment.type === 'video_autoplay' || attachment.type === 'video_inline') {
    return 'video';
  }

  if (attachment.type === 'photo') {
    return 'photo';
  }

  if (attachment.type === 'album') {
    return 'album';
  }

  return 'text';
};

const getPostTypeTag = (type: string) => {
  switch (type) {
    case 'text':
      return <Tag icon={<FileTextOutlined />} color="blue">Văn bản</Tag>;
    case 'photo':
      return <Tag icon={<PictureOutlined />} color="green">Ảnh</Tag>;
    case 'video':
      return <Tag icon={<VideoCameraOutlined />} color="magenta">Video</Tag>;
    case 'album':
      return <Tag icon={<PictureOutlined />} color="orange">Album ảnh</Tag>;
    default:
      return <Tag color="default">Khác</Tag>;
  }
};

const FacebookManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState<FacebookPost[]>([]);
  const [facebookConfig, setFacebookConfig] = useState<Facebook | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [selectedPost, setSelectedPost] = useState<FacebookPostDetail | null>(null);
  const [form] = Form.useForm();
  const facebookService = new FacebookService();
  const socialMediaPostService = new SocialMediaPostService();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [searchText, setSearchText] = useState('');
  const [postTypeFilter, setPostTypeFilter] = useState<'text' | 'photo' | 'video' | 'album' | ''>('');
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchFacebookConfig = async () => {
    try {
      const configs = await facebookService.getAllFacebookConfigs();
      if (configs.length > 0) {
        setFacebookConfig(configs[0]);
        return configs[0];
      }
      return null;
    } catch (error) {
      message.error('Không thể lấy cấu hình Facebook');
      return null;
    }
  };

  const handleAddConfig = async (values: Facebook) => {
    try {
      setLoading(true);
      await facebookService.addFacebookConfig(values);
      message.success('Thêm cấu hình Facebook thành công');
      setIsModalVisible(false);
      form.resetFields();
      const config = await fetchFacebookConfig();
      if (config) {
        fetchPosts(config);
      }
    } catch (error) {
      message.error('Không thể thêm cấu hình Facebook');
    } finally {
      setLoading(false);
    }
  };

  const fetchPostDetail = async (postId: string) => {
    if (!facebookConfig) return;

    try {
      // First check if we already have this post in our database
      // const existingPost = await socialMediaPostService.getPostByPostId(postId);
      // if (existingPost) {
      //   setSelectedPost(existingPost.detail as FacebookPostDetail);
      //   setIsDetailModalVisible(true);
      //   return;
      // }

      setLoading(true);
      const response = await axios.get<FacebookPostDetail>(
        `https://graph.facebook.com/v22.0/${postId}`,
        {
          params: {
            fields: 'id,message,created_time,permalink_url,attachments',
            access_token: facebookConfig.longToken
          }
        }
      );

      setSelectedPost(response.data);
      setIsDetailModalVisible(true);
    } catch (error) {
      message.error('Không thể lấy chi tiết bài viết');
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async (config: Facebook) => {
    setLoading(true);
    try {
      const response = await axios.get<FacebookResponse>(
        `https://graph.facebook.com/v22.0/${config.idPage}/feed`,
        {
          params: {
            access_token: config.longToken
          }
        }
      );

      console.log(response.data.data);

      setPosts(response.data.data);
    } catch (error) {
      message.error('Không thể lấy danh sách bài viết Facebook');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      const config = await fetchFacebookConfig();
      if (config) {
        fetchPosts(config);
      }
    };
    init();
  }, []);

  const getColumns = (): ColumnsType<FacebookPost> => {
    const baseColumns: ColumnsType<FacebookPost> = [
      {
        title: 'Thời gian tạo',
        dataIndex: 'created_time',
        key: 'created_time',
        width: 180,
        render: (text: string) => dayjs(text).format('DD/MM/YYYY HH:mm:ss')
      },
      {
        title: 'Nội dung',
        dataIndex: 'message',
        key: 'message',
        width: 400,
        render: (message: string, record: FacebookPost) => (
          <Typography.Paragraph ellipsis={{ rows: 3 }}>
            {message || record.story || 'Không có nội dung'}
          </Typography.Paragraph>
        )
      },
      // {
      //   title: 'Loại',
      //   key: 'type',
      //   width: 120,
      //   render: (_: any, record: FacebookPost) => getPostTypeTag(getPostType(record))
      // },
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 280
      },
      {
        title: 'Thao tác',
        key: 'action',
        fixed: 'right' as const,
        width: 250,
        render: (_: any, record: FacebookPost) => (
          <Space>
            <Button
              type="link"
              onClick={() => fetchPostDetail(record.id)}
              icon={<EyeOutlined />}
            >
              Chi tiết
            </Button>
            <Button
              type="link"
              href={`https://facebook.com/${record.id}`}
              target="_blank"
              icon={<FacebookOutlined />}
            >
              Xem trên Facebook
            </Button>
          </Space>
        )
      }
    ];

    return baseColumns;
  };

  const getMobileColumns = (): ColumnsType<FacebookPost> => [
    {
      title: 'Thông tin',
      dataIndex: 'message',
      key: 'info',
      render: (_: any, record: FacebookPost) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div>
            <div style={{ marginBottom: 8 }}>
              {getPostTypeTag(getPostType(record))}
              <span style={{ marginLeft: 8, fontSize: '12px', color: '#666' }}>
                {dayjs(record.created_time).format('DD/MM/YYYY HH:mm:ss')}
              </span>
            </div>
            <Typography.Paragraph ellipsis={{ rows: 3 }} style={{ marginBottom: 8 }}>
              {record.message || record.story || 'Không có nội dung'}
            </Typography.Paragraph>
          </div>
          <Space>
            <Button
              type="link"
              onClick={() => fetchPostDetail(record.id)}
              icon={<EyeOutlined />}
              size="small"
            >
              Chi tiết
            </Button>
            <Button
              type="link"
              href={`https://facebook.com/${record.id}`}
              target="_blank"
              icon={<FacebookOutlined />}
              size="small"
            >
              Xem trên Facebook
            </Button>
          </Space>
        </div>
      )
    }
  ];

  const handleRefresh = () => {
    if (facebookConfig) {
      fetchPosts(facebookConfig);
    }
  };

  const getFilteredPosts = () => {
    return posts.filter(post => {
      const matchesSearch = (post.message || post.story || '').toLowerCase().includes(searchText.toLowerCase());
      const matchesType = postTypeFilter ? (getPostType(post) === postTypeFilter) : true;
      return matchesSearch && matchesType;
    });
  };

  const renderPostContent = (post: FacebookPostDetail) => {
    const postType = getPostType(post);

    return (
      <div className="facebook-post-detail">
        <div className="post-header">
          <Space align="center" style={{ marginBottom: 24 }}>
            <div className="post-type-tag">
              {getPostTypeTag(postType)}
            </div>
            <div className="post-time">
              {dayjs(post.created_time).format('DD/MM/YYYY HH:mm:ss')}
            </div>
          </Space>
        </div>

        {post.message && (
          <div className="post-content">
            <Card bordered={false}>
              <Paragraph
                style={{
                  whiteSpace: 'pre-line',
                  fontSize: '16px',
                  margin: 0
                }}
              >
                {post.message}
              </Paragraph>
            </Card>
          </div>
        )}

        {post.attachments?.data.map((attachment, index) => (
          <div key={index} className="post-attachment">
            {(attachment.type === 'video_autoplay' || attachment.type === 'video_inline') && (
              <Card
                title={<Title level={5}><VideoCameraOutlined /> Video</Title>}
                bordered={false}
                className="attachment-card"
              >
                {attachment.media?.source ? (
                  <div className="video-container">
                    <video
                      controls
                      style={{ width: '100%', borderRadius: '8px' }}
                      poster={attachment.media.image?.src}
                    >
                      <source src={attachment.media.source} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                ) : (
                  <div className="video-fallback">
                    <Image
                      src={attachment.media?.image?.src}
                      alt="Video thumbnail"
                      style={{ borderRadius: '8px' }}
                    />
                    <Button
                      type="primary"
                      href={attachment.target?.url}
                      target="_blank"
                      icon={<FacebookOutlined />}
                      style={{ marginTop: 16 }}
                    >
                      Xem video trên Facebook
                    </Button>
                  </div>
                )}
              </Card>
            )}

            {attachment.type === 'photo' && attachment.media?.image && (
              <Card
                title={<Title level={5}><PictureOutlined /> Hình ảnh</Title>}
                bordered={false}
                className="attachment-card"
              >
                <Image
                  src={attachment.media.image.src}
                  alt="Post image"
                  style={{ borderRadius: '8px' }}
                />
              </Card>
            )}

            {attachment.type === 'album' && attachment.subattachments?.data && (
              <Card
                title={<Title level={5}><PictureOutlined /> Album ảnh</Title>}
                bordered={false}
                className="attachment-card"
              >
                <Row gutter={[16, 16]}>
                  {attachment.subattachments.data.map((subAttachment, subIndex) => (
                    <Col xs={24} sm={12} key={subIndex}>
                      <Image
                        src={subAttachment.media?.image?.src}
                        alt={`Album image ${subIndex + 1}`}
                        style={{
                          width: '100%',
                          height: '250px',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                      />
                    </Col>
                  ))}
                </Row>
              </Card>
            )}
          </div>
        ))}
      </div>
    );
  };

  const saveAllPosts = async () => {
    if (!facebookConfig || !posts.length) return;

    try {
      setLoading(true);
      let savedCount = 0;
      let existingCount = 0;

      for (const post of posts) {
        // Check if post already exists
        const existingPost = await socialMediaPostService.getPostByPostId(post.id);
        if (existingPost) {
          existingCount++;
          continue;
        }

        // Fetch full post details
        const response = await axios.get<FacebookPostDetail>(
          `https://graph.facebook.com/v22.0/${post.id}`,
          {
            params: {
              fields: 'id,message,created_time,permalink_url,attachments',
              access_token: facebookConfig.longToken
            }
          }
        );

        // Save to database
        await socialMediaPostService.addPost({
          type: 'facebook',
          postId: post.id,
          detail: response.data,
          createDate: new Date(response.data.created_time)
        });
        savedCount++;
      }

      message.success(`Đã lưu ${savedCount} bài viết mới (${existingCount} bài đã tồn tại)`);
    } catch (error) {
      message.error('Có lỗi xảy ra khi lưu bài viết');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAllPosts = () => {
    confirm({
      title: 'Xác nhận lưu bài viết',
      content: 'Bạn có chắc chắn muốn lưu tất cả bài viết vào cơ sở dữ liệu không?',
      okText: 'Đồng ý',
      cancelText: 'Hủy',
      onOk: saveAllPosts
    });
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1>Quản lý bài viết Facebook</h1>
          <Tag color="blue">{posts.length} bài viết</Tag>
        </div>

        <div className="admin-page-actions">
          <div className="admin-search-filters">
            <Search
              placeholder="Tìm kiếm nội dung"
              allowClear
              style={{ width: isMobile ? '100%' : 300 }}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Select
              placeholder="Lọc theo loại"
              style={{ width: isMobile ? '100%' : 200 }}
              allowClear
              onChange={(value) => setPostTypeFilter(value)}
            >
              <Select.Option value="text">
                <FileTextOutlined /> Văn bản
              </Select.Option>
              <Select.Option value="photo">
                <PictureOutlined /> Ảnh
              </Select.Option>
              <Select.Option value="video">
                <VideoCameraOutlined /> Video
              </Select.Option>
              <Select.Option value="album">
                <PictureOutlined /> Album ảnh
              </Select.Option>
            </Select>
          </div>

          <Space>
            {/* <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
            >
              Thêm cấu hình
            </Button> */}
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Làm mới
            </Button>
            <Button
              type="default"
              icon={<SaveOutlined />}
              onClick={handleSaveAllPosts}
              loading={loading}
            >
              Lưu tất cả bài viết
            </Button>
          </Space>
        </div>
      </div>

      <div className="admin-table-container">
        <Table
          className="admin-table"
          columns={isMobile ? getMobileColumns() : getColumns()}
          dataSource={getFilteredPosts()}
          rowKey="id"
          loading={loading}
          scroll={{ x: isMobile ? undefined : 1500 }}
          pagination={{
            responsive: true,
            defaultPageSize: isMobile ? 5 : 10,
            showSizeChanger: true,
            showTotal: (total) => `Tổng số ${total} bài viết`,
            position: isMobile ? ['bottomCenter'] : ['bottomRight']
          }}
        />
      </div>

      <Modal
        title="Thêm cấu hình Facebook"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddConfig}
        >
          <Form.Item
            name="appID"
            label="App ID"
            rules={[{ required: true, message: 'Vui lòng nhập App ID' }]}
          >
            <Input placeholder="Nhập App ID" />
          </Form.Item>

          <Form.Item
            name="appSecret"
            label="App Secret"
            rules={[{ required: true, message: 'Vui lòng nhập App Secret' }]}
          >
            <Input.Password placeholder="Nhập App Secret" />
          </Form.Item>

          <Form.Item
            name="idFacebook"
            label="ID Facebook"
            rules={[{ required: true, message: 'Vui lòng nhập ID Facebook' }]}
          >
            <Input placeholder="Nhập ID Facebook" />
          </Form.Item>

          <Form.Item
            name="idPage"
            label="ID Page"
            rules={[{ required: true, message: 'Vui lòng nhập ID Page' }]}
          >
            <Input placeholder="Nhập ID Page" />
          </Form.Item>

          <Form.Item
            name="longToken"
            label="Long-lived Token"
            rules={[{ required: true, message: 'Vui lòng nhập Long-lived Token' }]}
          >
            <Input.TextArea placeholder="Nhập Long-lived Token" rows={4} />
          </Form.Item>

          <Form.Item
            name="shortToken"
            label="Short-lived Token"
            rules={[{ required: true, message: 'Vui lòng nhập Short-lived Token' }]}
          >
            <Input placeholder="Nhập Short-lived Token" />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setIsModalVisible(false);
                form.resetFields();
              }}>
                Hủy
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Thêm
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <Space>
            <FacebookOutlined style={{ color: '#1677ff', fontSize: '20px' }} />
            <span>Chi tiết bài viết Facebook</span>
          </Space>
        }
        open={isDetailModalVisible}
        onCancel={() => {
          setIsDetailModalVisible(false);
          setSelectedPost(null);
        }}
        width={900}
        centered
        className="facebook-post-modal"
        footer={[
          <Button
            key="link"
            type="primary"
            href={selectedPost?.permalink_url}
            target="_blank"
            icon={<FacebookOutlined />}
          >
            Xem trên Facebook
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setIsDetailModalVisible(false);
              setSelectedPost(null);
            }}
          >
            Đóng
          </Button>
        ]}
      >
        {selectedPost && renderPostContent(selectedPost)}
      </Modal>
    </AdminLayout>
  );
};

export default FacebookManagement;